import React, { useRef, useMemo, useEffect } from "react";
import mesApi from "../../Api/mes.api";
import { t } from "../../i18n/locale";

const DAY_KEYS = ["Ned", "Pon", "Uto", "Sri", "Cet", "Pet", "Sub"];
const MONTH_KEYS = ["Sij", "Velj", "Ozu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];

// LocalStorage key for persisting selected date across MES pages
const MES_DATE_STORAGE_KEY = "mes_selected_date";

/** YYYY-MM-DD in local timezone (avoids UTC shift from toISOString). */
export const formatLocalDateIso = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Get default date - tomorrow for planning, today for other pages
export const getDefaultMesDate = (preferTomorrow = false): string => {
  const stored = localStorage.getItem(MES_DATE_STORAGE_KEY);
  if (stored) {
    // Validate stored date is reasonable (within -30 to +60 days)
    const storedDate = new Date(stored + "T12:00:00");
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diffDays = Math.floor((storedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays >= -30 && diffDays <= 60) {
      return stored;
    }
  }

  const base = new Date();
  if (preferTomorrow) {
    base.setDate(base.getDate() + 1);
  }
  return formatLocalDateIso(base);
};

// Save date to localStorage
export const saveMesDate = (date: string) => {
  localStorage.setItem(MES_DATE_STORAGE_KEY, date);
};

interface MesDateSelectorProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  showTimelineIndicators?: boolean;
  daysBack?: number;
  daysForward?: number;
}

const MesDateSelector: React.FC<MesDateSelectorProps> = ({
  selectedDate,
  onDateChange,
  showTimelineIndicators = false,
  daysBack = 14,
  daysForward = 30,
}) => {
  const dateScrollRef = useRef<HTMLDivElement>(null);
  const [dateTimelines, setDateTimelines] = React.useState<Record<string, "draft" | "confirmed">>({});

  // Generate array of dates for the horizontal selector
  const dateRange = useMemo(() => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Days back
    for (let i = daysBack; i > 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      dates.push(d);
    }

    // Today + days forward
    for (let i = 0; i <= daysForward; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }

    return dates;
  }, [daysBack, daysForward]);

  const scrollToSelectedDate = () => {
    const el = dateScrollRef.current;
    if (!el) return;

    const selectedIndex = dateRange.findIndex((d) => formatDateForApi(d) === selectedDate);
    if (selectedIndex < 0) return;

    const tileWidth = 72;
    const containerWidth = el.clientWidth;
    if (containerWidth <= 0) return;

    const maxScroll = Math.max(0, el.scrollWidth - containerWidth);
    const scrollPos = selectedIndex * tileWidth - containerWidth / 2 + tileWidth / 2;
    el.scrollLeft = Math.min(maxScroll, Math.max(0, scrollPos));
  };

  useEffect(() => {
    scrollToSelectedDate();
    const el = dateScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => scrollToSelectedDate());
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedDate, dateRange]);

  // Load timeline indicators if enabled
  useEffect(() => {
    if (showTimelineIndicators) {
      loadTimelinesForDateRange();
    }
  }, [showTimelineIndicators, dateRange]);

  const loadTimelinesForDateRange = async () => {
    try {
      const fromDate = formatDateForApi(dateRange[0]);
      const toDate = formatDateForApi(dateRange[dateRange.length - 1]);
      const timelines = await mesApi.getTimelines({ fromDate, toDate });

      const map: Record<string, "draft" | "confirmed"> = {};
      timelines.forEach((t) => {
        const date = t.ProductionDate?.split("T")[0];
        if (date) {
          // Prefer confirmed over draft if multiple exist
          if (!map[date] || t.Status === "confirmed") {
            map[date] = t.Status as "draft" | "confirmed";
          }
        }
      });
      setDateTimelines(map);
    } catch (err) {
      // Silently fail - date indicators are optional
    }
  };

  const formatDateForApi = formatLocalDateIso;

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const handleDateScroll = (direction: "left" | "right") => {
    if (dateScrollRef.current) {
      const scrollAmount = 300;
      dateScrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleDateSelect = (dateStr: string) => {
    saveMesDate(dateStr);
    onDateChange(dateStr);
  };

  // Method to update timeline status (can be called from parent)
  const updateTimelineStatus = (date: string, status: "draft" | "confirmed") => {
    setDateTimelines((prev) => ({ ...prev, [date]: status }));
  };

  return (
    <div className="date-selector-container">
      <button
        className="date-scroll-btn scroll-left"
        onClick={() => handleDateScroll("left")}
        aria-label="Scroll left"
      >
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>

      <div className="date-tiles-wrapper" ref={dateScrollRef}>
        <div className="date-tiles">
          {dateRange.map((date) => {
            const dateStr = formatDateForApi(date);
            const isSelected = dateStr === selectedDate;
            const timelineStatus = showTimelineIndicators ? dateTimelines[dateStr] : undefined;
            const classNames = [
              "date-tile",
              isSelected && "selected",
              isToday(date) && "today",
              isTomorrow(date) && "tomorrow",
              isPast(date) && "past",
              isWeekend(date) && "weekend",
              timelineStatus === "confirmed" && "has-timeline-confirmed",
              timelineStatus === "draft" && "has-timeline-draft",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={dateStr}
                className={classNames}
                onClick={() => handleDateSelect(dateStr)}
              >
                <span className="date-day-name">{t(DAY_KEYS[date.getDay()])}</span>
                <span className="date-day-number">{date.getDate()}</span>
                <span className="date-month">{t(MONTH_KEYS[date.getMonth()])}</span>
                {isToday(date) && <span className="date-label">{t("Danas")}</span>}
                {isTomorrow(date) && <span className="date-label">{t("Sutra")}</span>}
                {timelineStatus && (
                  <span
                    className={`date-timeline-indicator ${timelineStatus}`}
                    title={timelineStatus === "confirmed" ? t("Potvrđeno") : t("Draft")}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <button
        className="date-scroll-btn scroll-right"
        onClick={() => handleDateScroll("right")}
        aria-label="Scroll right"
      >
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      </button>
    </div>
  );
};

// Export the updateTimelineStatus functionality for parent components
export type MesDateSelectorRef = {
  updateTimelineStatus: (date: string, status: "draft" | "confirmed") => void;
};

export default MesDateSelector;
